import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Users, Star } from 'lucide-react';
import { bytes, when, type SortKey } from '../lib/format';
import type { DriveNode } from '../lib/types';
import { FileIcon } from './FileIcon';

/** 44 px rows: our touch-target floor (brand), still denser than Google Drive's 48 px (CEO, 4 Oct 2026). */
export const ROW_H = 44;
/** Phones (P1-4): the name gets its own line, with the date and size on a second line below it. */
const ROW_H_PHONE = 60;
const PHONE = '(max-width: 760px)';
const CARD_W = 184;
const CARD_H = 196;
const GAP = 12;
const OVERSCAN = 8;

export interface FileViewHandle { focus(): void; scrollToIndex(i: number): void; columns(): number }

interface Props {
  nodes: DriveNode[];
  mode: 'list' | 'grid';
  selected: Set<string>;
  active: number;
  label: string;
  sort: { key: SortKey; desc: boolean };
  onSort(k: SortKey): void;
  showOwner: boolean;
  trash: boolean;
  onPointerRow(i: number, e: MouseEvent): void;
  onOpen(i: number): void;
  onKeyDown(e: KeyboardEvent<HTMLDivElement>): void;
  /** Extra props for a row (right-click menu, drag and drop). */
  rowProps(i: number): Record<string, unknown>;
  empty?: ReactNode;
  dropOver: string | null;
  /** The row's own buttons at its end (hover actions and ⋮). */
  rowActions?(i: number): ReactNode;
}

/**
 * The file list: only the rows on screen exist in the page (a folder of 10,000 files stays as light
 * as one of 30). A WAI-ARIA multi-select listbox: focus stays on the list and `aria-activedescendant`
 * names the current row, so screen readers announce it and the keyboard works without tab stops on
 * every row.
 */
export const FileView = forwardRef<FileViewHandle, Props>(function FileView(p, ref) {
  const scroller = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 800, h: 600, top: 0 });
  const [phone, setPhone] = useState(() => matchMedia(PHONE).matches);
  useEffect(() => { const m = matchMedia(PHONE); const on = () => setPhone(m.matches); m.addEventListener('change', on); return () => m.removeEventListener('change', on); }, []);
  const listH = phone ? ROW_H_PHONE : ROW_H;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox((b) => ({ ...b, w: el.clientWidth, h: el.clientHeight })));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight, top: el.scrollTop });
    return () => ro.disconnect();
  }, []);
  const cols = p.mode === 'grid' ? Math.max(1, Math.floor((box.w - GAP) / (CARD_W + GAP))) : 1;
  const rowH = p.mode === 'grid' ? CARD_H + GAP : listH;
  const rows = Math.ceil(p.nodes.length / cols);
  const first = Math.max(0, Math.floor(box.top / rowH) - OVERSCAN);
  const last = Math.min(rows, Math.ceil((box.top + box.h) / rowH) + OVERSCAN);

  useImperativeHandle(ref, () => ({
    focus: () => scroller.current?.focus(),
    columns: () => cols,
    scrollToIndex: (i: number) => {
      const el = scroller.current;
      if (!el) return;
      const r = Math.floor(i / cols);
      const top = r * rowH;
      if (top < el.scrollTop) el.scrollTop = top;
      else if (top + rowH > el.scrollTop + el.clientHeight) el.scrollTop = top + rowH - el.clientHeight;
    },
  }), [cols, rowH]);

  const activeId = p.nodes[p.active] ? `row-${p.nodes[p.active].id}` : undefined;
  const items: ReactNode[] = [];
  for (let r = first; r < last; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const n = p.nodes[i];
      if (!n) break;
      const sel = p.selected.has(n.id);
      const style = p.mode === 'grid'
        ? { transform: `translate(${GAP + c * (CARD_W + GAP)}px, ${r * rowH + GAP}px)`, width: CARD_W, height: CARD_H }
        : { transform: `translateY(${r * rowH}px)`, height: listH };
      const common = {
        id: `row-${n.id}`,
        role: 'option',
        'aria-selected': sel,
        'aria-setsize': p.nodes.length,
        'aria-posinset': i + 1,
        'aria-label': `${n.name}, ${n.kind === 'folder' ? 'folder' : bytes(n.size)}${n.starred ? ', starred' : ''}${n.shared ? ', shared' : ''}`,
        className: `${p.mode === 'grid' ? 'card' : 'row'}${sel ? ' is-selected' : ''}${i === p.active ? ' is-active' : ''}${p.dropOver === n.id ? ' is-over' : ''}`,
        style,
        'data-testid': 'file-row',
        'data-name': n.name,
        'data-kind': n.kind,
        onMouseDown: (e: MouseEvent) => p.onPointerRow(i, e),
        onDoubleClick: () => p.onOpen(i),
        ...p.rowProps(i),
      };
      items.push(p.mode === 'grid' ? (
        <div key={n.id} {...common}>
          <div className="card__thumb">{n.thumb ? <img src={n.thumb} alt="" loading="lazy" decoding="async" draggable={false} /> : <FileIcon node={n} size={48} />}</div>
          <div className="card__name"><FileIcon node={n} size={16} /><span>{n.name}</span>{n.deletion && <span className="row__state" data-testid="deletion-state">{n.deletion === 'waiting' ? 'Waiting to delete' : 'Deleting'}</span>}{n.starred && <Star aria-hidden size={14} className="star" />}</div>
        </div>
      ) : (
        <div key={n.id} {...common}>
          <span className="row__name"><FileIcon node={n} size={20} /><span className="row__text">{n.name}</span>{n.deletion && <span className="row__state" data-testid="deletion-state">{n.deletion === 'waiting' ? 'Waiting to delete' : 'Deleting'}</span>}{n.starred && <Star aria-hidden size={14} className="star" />}{n.shared && <Users aria-hidden size={14} className="muted" />}{n.scan === 'infected' && <span className="dz-badge row__warn">Virus</span>}</span>
          {p.showOwner && <span className="row__owner">{n.owner.you ? 'me' : n.owner.name || n.owner.email}</span>}
          <span className="row__date">{p.trash && n.trashedAt ? when(n.trashedAt) : when(n.modifiedAt)}</span>
          <span className="row__size">{n.kind === 'folder' ? '—' : bytes(n.size)}</span>
          {p.rowActions && <span className="row__acts" aria-hidden="true" />}
        </div>
      ));
      // The row's buttons sit over its end, outside the option (an option can't hold controls) and out of
      // the accessibility tree (a listbox holds only options); the keyboard has the same actions on the row.
      // Hover and selection on the row show them (app.css).
      if (p.mode === 'list' && p.rowActions) items.push(<div key={`${n.id}-acts`} className="rowacts" aria-hidden="true" style={{ transform: `translateY(${r * rowH}px)`, height: listH }}>{p.rowActions(i)}</div>);
    }
  }

  const head = (k: SortKey, label: string, cls: string) => (
    <button type="button" className={`head__btn ${cls}`} onClick={() => p.onSort(k)} aria-label={`Sort by ${label.toLowerCase()}${p.sort.key === k ? (p.sort.desc ? ', descending' : ', ascending') : ''}`} data-testid={`sort-${k}`}>
      {label}{p.sort.key === k && (p.sort.desc ? <ArrowDown aria-hidden size={14} /> : <ArrowUp aria-hidden size={14} />)}
    </button>
  );

  return (
    <div className="files">
      {p.mode === 'list' && p.nodes.length > 0 && (
        <div className={`head${p.showOwner ? '' : ' head--no-owner'}`}>
          {head('name', 'Name', 'row__name')}
          {p.showOwner && <span className="row__owner head__label">Owner</span>}
          {head('modified', p.trash ? 'Deleted' : 'Last modified', 'row__date')}
          {head('size', 'File size', 'row__size')}
          {p.rowActions && <span className="row__acts" aria-hidden="true" />}
        </div>
      )}
      <div ref={scroller} className={`files__scroll files__scroll--${p.mode}${p.showOwner ? '' : ' no-owner'}`} tabIndex={p.nodes.length ? 0 : -1} role={p.nodes.length ? 'listbox' : undefined} aria-multiselectable={p.nodes.length ? true : undefined} aria-label={p.nodes.length ? p.label : undefined}
        aria-activedescendant={activeId} onKeyDown={p.onKeyDown} data-testid="file-list" data-count={p.nodes.length}
        onScroll={(e) => { const t = (e.target as HTMLDivElement).scrollTop; requestAnimationFrame(() => setBox((b) => (b.top === t ? b : { ...b, top: t }))); }}>
        {p.nodes.length === 0 ? p.empty : (
          <div className="files__space" style={{ height: rows * rowH + (p.mode === 'grid' ? GAP : 0) }}>{items}</div>
        )}
      </div>
    </div>
  );
});
